<template>
    <v-dialog v-model="dialogModel" content-class="haq-dialog" max-width="640px" @click:outside="closeModal">
        <v-card class="haq-dialog-card">
            <v-card-title class="haq-dialog-card-header">
                <h5>Create New Dashboard</h5>
                <v-spacer />
                <v-icon class="close-icon" @click="closeModal">mdi-close</v-icon>
            </v-card-title>
            <v-card-text class="create-dashboard-fields">
                <div class="create-templates">
                    <div class="create-templates-title">Templates</div>
                    <div class="create-templates-grid">
                        <button
                            v-for="tpl in templates"
                            :key="tpl.id"
                            type="button"
                            class="create-template-card"
                            :class="{ 'is-selected': templateId === tpl.id }"
                            @click="selectTemplate(tpl)"
                        >
                            <v-icon>{{ tpl.pickerIcon || tpl.icon }}</v-icon>
                            <span>{{ tpl.name }}</span>
                        </button>
                    </div>
                </div>
                <HaqTextField v-model="formData.name" label="Dashboard Name" hide-details />
                <HaqSelect
                    v-model="formData.defaultSite"
                    :items="availableSites"
                    item-text="name"
                    item-value="objectId"
                    label="Default Site"
                    hide-details
                    clearable
                    @change="onSiteChange"
                />
                <HaqSelect
                    v-if="formData.defaultSite"
                    v-model="formData.defaultZone"
                    :items="availableZones"
                    item-text="name"
                    item-value="objectId"
                    label="Default Zone (Optional)"
                    hide-details
                    clearable
                />
                <HaqTextarea v-model="formData.description" label="Description (Optional)" hide-details rows="3" />
                <p v-if="dashboardError" class="create-dashboard-error">{{ dashboardError }}</p>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn class="haq-btn haq-btn-secondary" @click="closeModal">Cancel</v-btn>
                <v-btn
                    class="haq-btn haq-btn-primary"
                    @click="handleSubmit"
                    :disabled="!isFormValid || dashboardLoading"
                    :loading="dashboardLoading"
                >
                    {{ dashboardLoading ? 'Creating...' : 'Create Dashboard' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import HaqSelect from '@/components/form/HaqSelect.vue'
import HaqTextField from '@/components/form/HaqTextField.vue'
import HaqTextarea from '@/components/form/HaqTextarea.vue'
import { listDashboardTemplates, cloneDashboardTemplate } from './v2/dashboardModel'

export default {
    name: 'CreateDashboard',
    components: {
        HaqSelect,
        HaqTextField,
        HaqTextarea,
    },
    props: {
        dialog: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            templateId: 'custom',
            templates: listDashboardTemplates(),
            formData: {
                name: '',
                description: '',
                defaultSite: null,
                defaultZone: null
            },
            availableSites: [],
            availableZones: [],
            sitesLoading: false,
            zonesLoading: false
        }
    },
    computed: {
        dialogModel: {
            get() {
                return this.dialog;
            },
            set(value) {
                this.$emit('update:dialog', value);
            }
        },
        ...mapState({
            projects: state => state.projects,
            dashboardLoading: state => state.dashboardLoading,
            dashboardError: state => state.dashboardError
        }),
        /**
         * Current project name from the route or store.
         * @returns {string}
         */
        currentProjectName() {
            const projectId = this.$route.params.projectId;
            if (projectId && this.projects.length > 0) {
                const currentProject = this.projects.find(p => p.objectId === projectId);
                return currentProject ? currentProject.name : 'Project';
            }
            return this.projects.length > 0 ? this.projects[0].name : 'Project';
        },
        /**
         * True when the create form has a name.
         * @returns {boolean}
         */
        isFormValid() {
            return this.formData.name.trim() !== '';
        }
    },
    methods: {
        ...mapActions(['createDashboard']),
        /**
         * Close the dialog and reset the form.
         */
        closeModal() {
            this.$emit('update:dialog', false);
            this.$emit('close');
            this.resetForm();
        },
        /**
         * Clear create-form fields.
         */
        resetForm() {
            this.templateId = 'custom';
            this.formData = {
                name: '',
                description: '',
                defaultSite: null,
                defaultZone: null
            };
            this.availableZones = [];
        },
        /**
         * Select a create template.
         * @param {object} tpl
         */
        selectTemplate(tpl) {
            if (!tpl || !tpl.id) return;
            this.templateId = tpl.id;
        },
        /**
         * Create the dashboard and close on success.
         */
        async handleSubmit() {
            console.log('handleSubmit', this.formData);
            try {
                let projectId = this.$route.params.projectId;

                if (!projectId) {
                    if (this.$store.state.project && this.$store.state.project.objectId) {
                        projectId = this.$store.state.project.objectId;
                    } else if (this.projects.length > 0) {
                        projectId = this.projects[0].objectId;
                    }
                }

                if (!projectId) {
                    throw new Error('No project ID available');
                }

                const tpl = this.templates.find(t => t.id === this.templateId) || this.templates[0];
                const cloned = cloneDashboardTemplate(tpl);
                const newDashboard = {
                    name: this.formData.name,
                    description: this.formData.description || cloned.description,
                    level: 'project',
                    scope: 'Project Level Dashboard',
                    sections: cloned.sections,
                    cards: cloned.cards,
                    schemaVersion: 2,
                    icon: cloned.icon,
                    defaultTimeRange: cloned.defaultTimeRange,
                    project: projectId,
                    site: this.formData.defaultSite || null,
                    zone: this.formData.defaultZone || null
                };

                const result = await this.createDashboard(newDashboard);
                this.$emit('create-dashboard', result.results);
                this.closeModal();
            } catch (error) {
                console.error('Failed to create dashboard:', error);
                window.M.toast({ html: 'Failed to create dashboard' });
            }
        },
        /**
         * Load accessible sites for the default-site select.
         */
        async loadSites() {
            if (this.availableSites.length === 0) {
                this.sitesLoading = true;
                try {
                    const { HTTPClient } = await import('../../assets/js/http');
                    const client = new HTTPClient();
                    const sitesRes = await client.robustQuery('/api', { slim: true });
                    this.availableSites = (sitesRes.sites || []).sort((a, b) => a.name.localeCompare(b.name));
                } catch (error) {
                    console.error('Failed to load sites:', error);
                    this.availableSites = [];
                } finally {
                    this.sitesLoading = false;
                }
            }
        },
        /**
         * Reload zones when the default site changes.
         * @param {string|null} siteId
         */
        async onSiteChange(siteId) {
            this.formData.defaultZone = null;
            this.availableZones = [];

            if (siteId) {
                this.zonesLoading = true;
                try {
                    const { HTTPClient } = await import('../../assets/js/http');
                    const client = new HTTPClient();
                    const zonesRes = await client.robustQuery('/api/zones', { siteId });
                    this.availableZones = (zonesRes.results || []).sort((a, b) => a.name.localeCompare(b.name));
                } catch (error) {
                    console.error('Failed to load zones for site:', error);
                    this.availableZones = [];
                } finally {
                    this.zonesLoading = false;
                }
            }
        }
    },
    watch: {
        dialog(val) {
            if (val) {
                this.loadSites();
            } else {
                this.resetForm();
            }
        }
    },
    mounted() {
        if (this.dialog) {
            this.loadSites();
        }
    }
}
</script>

<style lang="sass" scoped>
.create-dashboard-fields
    padding-bottom: 20px !important
    .haq-field
        margin-bottom: 20px

.create-templates
    margin-bottom: 20px

.create-templates-title
    font-size: 15px
    color: var(--app-primary-txt)
    margin-bottom: 10px

.create-templates-grid
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 12px

.create-template-card
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 10px
    min-height: 120px
    padding: 20px 12px
    border: 1px solid rgba(168, 174, 222, 0.18)
    border-radius: 14px
    background: var(--card-primary-bg)
    color: var(--app-primary-txt)
    cursor: pointer
    text-align: center
    .v-icon
        color: var(--score-blue-color) !important
        font-size: 36px
    span
        font-size: 16px
    &:hover
        border-color: rgba(91, 141, 239, 0.5)
    &.is-selected
        border-color: var(--score-blue-color)

.create-dashboard-error
    color: var(--score-red-color)
    text-align: center
    margin: 12px 0 0
</style>
